import { Fragment } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToggle, useUpdateEffect } from 'react-use';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, FormHelperText, Stack, styled, Typography } from '@mui/material';
import i18next from 'i18next';
import type { InferType } from 'yup';

import type { FieldMatchingSettings, FieldMatchingSettingsColumn } from '@/api/domains/field-matching.types';
import { RoundedButton } from '@/components/buttons';
import { InfoDialog } from '@/components/dialogs/InfoDialog';
import { Form, RHFIntegerTextField } from '@/components/form-controls';
import { DataGridContainer, DataGridToolbar } from '@/components/ReactDataGrid';
import { PageContent } from '@/layouts/Page';
import { Yup } from '@/shared/lib/yup';

import { useUploadedFileColumnsNumber, useUploadedFileRowsNumber } from './field-matching.store';

// ----------------------------------------------------------------------

const Footer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  height: theme.dimensions.summaryBar.height,
  padding: theme.spacing(1, 4),
  backgroundColor: theme.palette.background.paper,
}));

const StartRow = styled(RHFIntegerTextField)({
  width: 52,

  '& .MuiInputBase-input': {
    textAlign: 'center',
  },
});

// ----------------------------------------------------------------------

export type FieldMatchingFormProps = React.PropsWithChildren & {
  fieldMatchingSettings?: FieldMatchingSettings;
  onSubmit: (formData: FormSchema) => void;
  onCancel: () => void;
  isLoading: boolean;
  requiredColumnsDialogTitle?: string;
  requiredColumnsDialogMessage: React.ReactNode;
  validateColumns?: (columns: Record<string, string>) => boolean;
  renderWarningDialog?: (props: { open: boolean; onClose: () => void }) => React.ReactNode;
};

const formSchema = Yup.object()
  .shape({
    startRow: Yup.string().trim(),
    rowsNumber: Yup.number().required(),
  })
  .test(
    'startRow',
    i18next.t('field-matching-form.start-row', { ns: 'field-matching' }),
    function (value, { createError }) {
      const { startRow, rowsNumber } = value;

      if (startRow && rowsNumber) {
        if (Number(startRow) > rowsNumber || Number(startRow) < 1) {
          return createError({ path: 'rowsNumber' });
        }
      }

      if (!startRow) {
        return createError({ path: 'rowsNumber' });
      }
      return true;
    },
  );

export type FormSchema = InferType<typeof formSchema> & {
  columns: Record<`column-${number}`, string>;
};

const getDefaultValuesFromColumns = (columns: FieldMatchingSettingsColumn[], fileColumnsNumber: number) =>
  columns.reduce<FormSchema['columns']>((acc, { key, number }) => {
    if (number === 0 || number > fileColumnsNumber) return acc;

    acc[`column-${number}`] = key;

    return acc;
  }, {});

export const FieldMatchingForm = (props: FieldMatchingFormProps) => {
  const { t } = useTranslation('field-matching');
  const {
    children,
    fieldMatchingSettings,
    onSubmit,
    isLoading: passedIsLoading,
    requiredColumnsDialogTitle = t('field-matching-form.required-columns-dialog-title'),
    requiredColumnsDialogMessage,
    validateColumns,
    onCancel,
    renderWarningDialog,
  } = props;

  const [isWarningDialogOpen, setIsWarningDialogOpen] = useToggle(false);
  const closeWarningDialog = () => setIsWarningDialogOpen(false);
  const openWarningDialog = () => setIsWarningDialogOpen(true);
  const uploadedFileColumnsNumber = useUploadedFileColumnsNumber();
  const uploadedFileRowsNumber = useUploadedFileRowsNumber();

  const shouldPrefillValues = !!fieldMatchingSettings;
  const defaultValues = {
    columns: shouldPrefillValues
      ? getDefaultValuesFromColumns(fieldMatchingSettings.columns, uploadedFileColumnsNumber)
      : {},
    startRow: shouldPrefillValues ? String(fieldMatchingSettings.start_row) : '1',
    rowsNumber: uploadedFileRowsNumber,
  };
  const methods = useForm<FormSchema>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  const {
    reset,
    formState: { isSubmitting, errors },
  } = methods;

  useUpdateEffect(() => {
    if (fieldMatchingSettings) {
      reset(defaultValues);
    }
  }, [fieldMatchingSettings, uploadedFileColumnsNumber]);

  const submitHandler: SubmitHandler<FormSchema> = async formData => {
    if (validateColumns && !validateColumns(formData.columns)) {
      openWarningDialog();
      return;
    }

    onSubmit(formData);
  };

  const isLoading = isSubmitting || passedIsLoading;

  return (
    <Fragment>
      <FormProvider {...methods}>
        <Form
          onSubmit={methods.handleSubmit(submitHandler)}
          sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}
        >
          <PageContent>
            <DataGridContainer>
              <DataGridToolbar
                slotProps={{
                  contentContainer: {
                    divider: null,
                  },
                }}
                pl={2}
              >
                <Typography>{t('field-matching-form.start-row')}</Typography>

                <StartRow name="startRow" id="startRow" variant="standard" />
                <FormHelperText error={!!errors}>{errors.rowsNumber?.message}</FormHelperText>
              </DataGridToolbar>
              {children}
            </DataGridContainer>
          </PageContent>

          <Footer>
            <Stack spacing={1} direction="row">
              <RoundedButton fullWidth variant="outlined" sx={{ maxWidth: 176 }} onClick={onCancel}>
                {t('field-matching-form.actions.cancel')}
              </RoundedButton>
              <RoundedButton
                fullWidth
                loading={isLoading}
                type="submit"
                variant="contained"
                sx={{ maxWidth: 176 }}
                data-testid="field-matching-continue-btn"
              >
                {t('field-matching-form.actions.submit')}
              </RoundedButton>
            </Stack>
          </Footer>
        </Form>
      </FormProvider>

      {renderWarningDialog ? (
        renderWarningDialog({ open: isWarningDialogOpen, onClose: closeWarningDialog })
      ) : (
        <InfoDialog
          open={isWarningDialogOpen}
          onClose={closeWarningDialog}
          headerText={requiredColumnsDialogTitle}
          bodyMessage={requiredColumnsDialogMessage}
        />
      )}
    </Fragment>
  );
};
